/**
 * Zod Schemas for Match Flow Validation
 *
 * Used for validating flow definitions and configurations.
 */

import { z } from "zod";

// =============================================================================
// EDGE CONDITION SCHEMA
// =============================================================================

export const flowEdgeConditionSchema = z.object({
  field: z.string().min(1),
  operator: z.enum(["eq", "neq", "gt", "gte", "lt", "lte", "in", "notIn", "exists"]),
  value: z.unknown(),
});

// =============================================================================
// EDGE SCHEMA
// =============================================================================

export const flowEdgeSchema = z.object({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  sourceHandle: z.string().optional(), // Which output handle this edge connects from
  condition: flowEdgeConditionSchema.optional(),
});

// =============================================================================
// NODE POSITION SCHEMA
// =============================================================================

export const nodePositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

// =============================================================================
// NODE DEFINITION SCHEMA
// =============================================================================

export const flowNodeDefinitionSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  label: z.string().min(1).max(50),
  position: nodePositionSchema,
  config: z.record(z.string(), z.unknown()),
});

// =============================================================================
// FLOW DEFINITION SCHEMA
// =============================================================================

export const flowDefinitionSchema = z.object({
  nodes: z.array(flowNodeDefinitionSchema).min(1),
  edges: z.array(flowEdgeSchema),
  startNodeId: z.string().min(1),
  version: z.number().int().positive(),
});

// =============================================================================
// SHARED LABELS SCHEMA
// =============================================================================

/** Reusable labels schema for admin-customizable node headings. */
const nodeLabelsSchema = z
  .object({
    title: z.string().max(50).optional(),
    subtitle: z.string().max(100).optional(),
  })
  .optional();

// =============================================================================
// BUILT-IN NODE CONFIG SCHEMAS
// =============================================================================

export const readyUpConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(10)
    .max(120)
    .default(30)
    .describe("How long players have to ready up before the match is cancelled."),
  requiredPercentage: z
    .number()
    .min(0.5)
    .max(1)
    .default(1.0)
    .describe("Percentage of players that must ready up. 100% means everyone must accept."),
  labels: z
    .object({
      title: z.string().max(50).optional(),
      subtitle: z.string().max(100).optional(),
      buttonText: z.string().min(1).max(20).optional(),
    })
    .default({}),
});

export const mapVoteConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(15)
    .max(120)
    .default(45)
    .describe("How long players have to cast their vote."),
  tiebreaker: z
    .enum(["random", "first_vote"])
    .default("random")
    .describe("How to break a tie. Random picks randomly; First Vote favors the earliest vote."),
  showVotes: z
    .enum(["hidden", "count_only", "full"])
    .default("count_only")
    .describe(
      "What players see during voting. Hidden shows nothing, Count Only shows totals, Full reveals who voted for what.",
    ),
  mapPool: z
    .array(z.string())
    .optional()
    .describe("Available maps for voting. Uses the queue map pool if not set."),
  labels: nodeLabelsSchema,
});

export const mapBanConfigSchema = z.object({
  timeoutSecondsPerBan: z
    .number()
    .int()
    .min(10)
    .max(60)
    .default(30)
    .describe("Time each team gets per ban."),
  bansPerTeam: z
    .number()
    .int()
    .min(1)
    .max(5)
    .default(2)
    .describe("How many maps each team can ban."),
  banOrder: z
    .enum(["alternating", "simultaneous"])
    .default("alternating")
    .describe("Alternating means teams take turns; Simultaneous means both ban at the same time."),
  labels: nodeLabelsSchema,
});

export const autoBalanceConfigSchema = z.object({
  algorithm: z
    .enum(["greedy", "snake"])
    .default("greedy")
    .describe(
      "Greedy puts the next best player on the weaker team. Snake alternates pick order each round for fairer teams.",
    ),
  labels: nodeLabelsSchema,
});

export const captainDraftConfigSchema = z.object({
  timeoutSecondsPerPick: z
    .number()
    .int()
    .min(10)
    .max(60)
    .default(30)
    .describe("Time each captain gets to make a pick."),
  captainSelection: z
    .enum(["highest_elo", "random", "volunteer"])
    .default("highest_elo")
    .describe(
      "How captains are chosen. Highest ELO picks the top-rated players; Volunteer lets players opt in.",
    ),
  draftOrder: z
    .enum(["snake", "linear"])
    .default("snake")
    .describe(
      "Snake reverses pick order each round (A-B-B-A). Linear keeps the same order (A-B-A-B).",
    ),
  labels: nodeLabelsSchema,
});

export const selfSelectConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(30)
    .max(180)
    .default(60)
    .describe("How long players have to pick a team."),
  maxPerTeam: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe("Maximum players allowed on each team. Leave empty for no limit."),
  allowUneven: z
    .boolean()
    .default(false)
    .describe("Allow teams to have different numbers of players."),
  labels: nodeLabelsSchema,
});

// -----------------------------------------------------------------------------
// SELECTION PHASE NODES
// -----------------------------------------------------------------------------

export const gameModeSelectConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(15)
    .max(120)
    .default(30)
    .describe("How long players have to select a game mode."),
  selectionMethod: z
    .enum(["vote", "host_choice", "random"])
    .default("vote")
    .describe(
      "Vote lets players choose; Host Choice lets the host decide; Random picks one automatically.",
    ),
  gameModes: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().optional(),
      }),
    )
    .optional()
    .describe("Available game modes. If empty, players see a generic selection."),
  labels: nodeLabelsSchema,
});

export const characterBanConfigSchema = z.object({
  timeoutSecondsPerBan: z
    .number()
    .int()
    .min(10)
    .max(60)
    .default(30)
    .describe("Time each team gets per ban."),
  bansPerTeam: z
    .number()
    .int()
    .min(1)
    .max(5)
    .default(2)
    .describe("How many characters each team can ban."),
  banOrder: z
    .enum(["alternating", "simultaneous", "snake"])
    .default("alternating")
    .describe(
      "Alternating means teams take turns. Simultaneous means both ban at once. Snake reverses order each round.",
    ),
  characterPool: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        imageUrl: z.string().optional(),
      }),
    )
    .optional()
    .describe("Available characters for banning. If empty, uses the game default roster."),
  labels: nodeLabelsSchema,
});

export const characterPickConfigSchema = z.object({
  timeoutSecondsPerPick: z
    .number()
    .int()
    .min(10)
    .max(60)
    .default(30)
    .describe("Time each player gets to pick a character."),
  pickOrder: z
    .enum(["alternating", "snake", "simultaneous"])
    .default("snake")
    .describe(
      "Alternating means teams take turns. Snake reverses order each round. Simultaneous means everyone picks at once.",
    ),
  allowDuplicates: z
    .boolean()
    .default(false)
    .describe("Allow multiple players to pick the same character."),
  characterPool: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        imageUrl: z.string().optional(),
      }),
    )
    .optional()
    .describe("Available characters for picking. If empty, uses the game default roster."),
  labels: nodeLabelsSchema,
});

export const sideSelectConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(10)
    .max(60)
    .default(20)
    .describe("How long the selecting team has to choose a side."),
  selectionMethod: z
    .enum(["winner_choice", "loser_choice", "coin_flip", "host_choice"])
    .default("coin_flip")
    .describe(
      "Who picks the side. Coin Flip is random; Winner/Loser Choice lets the previous round result decide.",
    ),
  sides: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().optional(),
      }),
    )
    .default([
      { id: "side_a", name: "Side A" },
      { id: "side_b", name: "Side B" },
    ])
    .describe("The available sides (e.g. Attack/Defense, CT/T). Defaults to Side A and Side B."),
  labels: nodeLabelsSchema,
});

// -----------------------------------------------------------------------------
// HOST & LOBBY NODES
// -----------------------------------------------------------------------------

export const hostInstructionsConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(30)
    .max(600)
    .default(120)
    .describe("How long the host has to complete the instructions before the match times out."),
  instructions: z
    .string()
    .default("Create a custom lobby and invite all players.")
    .describe(
      "Main instructions shown to the host. Use {{variable}} for dynamic values like {{selectedMapName}}, {{selectedGameMode}}, {{selectedServer}}.",
    ),
  customInstructions: z
    .array(z.string())
    .optional()
    .describe(
      "Additional step-by-step instructions shown as a checklist. Supports {{variable}} placeholders.",
    ),
  showPlayerList: z
    .boolean()
    .default(true)
    .describe("Show the list of players who need to join the lobby."),
  requireHostConfirmation: z
    .boolean()
    .default(true)
    .describe(
      "Require the host to confirm they have completed the instructions before proceeding.",
    ),
  labels: nodeLabelsSchema,
});

export const lobbyCodeFieldSchema = z.object({
  key: z.string().min(1).describe("Unique key used as the context variable name."),
  label: z.string().min(1).describe("Label shown above the input field."),
  placeholder: z.string().optional().describe("Placeholder text for this field."),
  type: z
    .enum(["text", "password"])
    .default("text")
    .describe("Input type — use password for fields that should be masked."),
});

export const lobbyCodeInputConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(30)
    .max(300)
    .default(120)
    .describe("How long the host has to enter the lobby details."),
  fields: z
    .array(lobbyCodeFieldSchema)
    .min(1)
    .default([{ key: "lobbyCode", label: "Lobby Code", type: "text" as const }])
    .refine((fields) => new Set(fields.map((f) => f.key)).size === fields.length, {
      message: "Field keys must be unique",
    })
    .describe("Input fields the host must fill in. Each field value is added to flow context."),
  instructions: z
    .string()
    .default("Host: Enter the lobby code so players can join.")
    .describe(
      "Instructions shown to the host above the inputs. Supports {{variable}} placeholders.",
    ),
  labels: nodeLabelsSchema,
});

export const lobbyCreatedConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(10)
    .max(120)
    .default(30)
    .describe("How long players have to join the lobby before the match times out."),
  showLobbyCode: z
    .boolean()
    .default(true)
    .describe("Display the lobby code to all players so they can join."),
  instructions: z
    .string()
    .default("Lobby is ready. Players may now join.")
    .describe("Instructions shown to players. Supports {{variable}} placeholders."),
  requireAllPlayersConfirm: z
    .boolean()
    .default(false)
    .describe("Require every player to confirm they have joined the lobby before proceeding."),
  labels: nodeLabelsSchema,
});

// -----------------------------------------------------------------------------
// COMMUNICATION NODES (Discord integration)
// -----------------------------------------------------------------------------

export const voiceChannelJoinConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(10)
    .max(120)
    .default(30)
    .describe("How long players have to join the voice channel."),
  channelName: z
    .string()
    .default("Match #{{matchId}}")
    .describe("Name of the voice channel to create. Use {{matchId}} to include the match ID."),
  autoMove: z
    .boolean()
    .default(true)
    .describe("Automatically move players into the voice channel via the Discord bot."),
  requireConfirmation: z
    .boolean()
    .default(false)
    .describe("Require players to confirm they have joined the voice channel."),
  labels: nodeLabelsSchema,
});

export const teamVoiceSplitConfigSchema = z.object({
  timeoutSeconds: z
    .number()
    .int()
    .min(10)
    .max(120)
    .default(30)
    .describe("How long players have to join their team voice channel."),
  teamChannelNames: z
    .array(z.string())
    .default(["Team 1", "Team 2"])
    .describe("Names for each team voice channel."),
  autoMove: z
    .boolean()
    .default(true)
    .describe("Automatically move players into their team voice channel via the Discord bot."),
  createSpectatorChannel: z
    .boolean()
    .default(false)
    .describe("Create an additional voice channel for spectators."),
  labels: nodeLabelsSchema,
});

// -----------------------------------------------------------------------------
// TERMINAL NODES
// -----------------------------------------------------------------------------

export const matchStartConfigSchema = z.object({
  countdownSeconds: z
    .number()
    .int()
    .min(0)
    .max(30)
    .default(5)
    .describe("Countdown duration before the match officially starts. Set to 0 to skip."),
  displayMessage: z
    .string()
    .default("Match is starting!")
    .describe("Message shown to all players when the match begins."),
  showTeams: z.boolean().default(true).describe("Show team rosters on the match start screen."),
  showMap: z.boolean().default(true).describe("Show the selected map on the match start screen."),
  labels: nodeLabelsSchema,
});

export const matchCancelledConfigSchema = z.object({
  reason: z
    .enum(["timeout", "not_enough_players", "host_cancelled", "error"])
    .optional()
    .describe("Default cancellation reason shown to players."),
  displayMessage: z
    .string()
    .default("Match has been cancelled.")
    .describe("Message shown to all players when the match is cancelled."),
  showReturnButton: z
    .boolean()
    .default(true)
    .describe("Show a button that lets players return to the queue or lobby."),
  labels: nodeLabelsSchema,
});

// =============================================================================
// NODE CONFIG SCHEMA MAP
// =============================================================================

export const nodeConfigSchemas: Record<string, z.ZodSchema> = {
  // Setup
  ready_up: readyUpConfigSchema,
  // Selection
  map_vote: mapVoteConfigSchema,
  map_ban: mapBanConfigSchema,
  game_mode_select: gameModeSelectConfigSchema,
  character_ban: characterBanConfigSchema,
  character_pick: characterPickConfigSchema,
  side_select: sideSelectConfigSchema,
  // Teams
  auto_balance: autoBalanceConfigSchema,
  captain_draft: captainDraftConfigSchema,
  self_select: selfSelectConfigSchema,
  // Host & Lobby
  host_instructions: hostInstructionsConfigSchema,
  lobby_code_input: lobbyCodeInputConfigSchema,
  lobby_created: lobbyCreatedConfigSchema,
  // Communication
  voice_channel_join: voiceChannelJoinConfigSchema,
  team_voice_split: teamVoiceSplitConfigSchema,
  // Terminal
  match_start: matchStartConfigSchema,
  match_cancelled: matchCancelledConfigSchema,
};

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type FlowEdgeCondition = z.infer<typeof flowEdgeConditionSchema>;
export type FlowEdge = z.infer<typeof flowEdgeSchema>;
export type NodePosition = z.infer<typeof nodePositionSchema>;
export type FlowNodeDefinition = z.infer<typeof flowNodeDefinitionSchema>;
export type FlowDefinition = z.infer<typeof flowDefinitionSchema>;

// Setup
export type ReadyUpConfig = z.infer<typeof readyUpConfigSchema>;

// Selection
export type MapVoteConfig = z.infer<typeof mapVoteConfigSchema>;
export type MapBanConfig = z.infer<typeof mapBanConfigSchema>;
export type GameModeSelectConfig = z.infer<typeof gameModeSelectConfigSchema>;
export type CharacterBanConfig = z.infer<typeof characterBanConfigSchema>;
export type CharacterPickConfig = z.infer<typeof characterPickConfigSchema>;
export type SideSelectConfig = z.infer<typeof sideSelectConfigSchema>;
// Teams
export type AutoBalanceConfig = z.infer<typeof autoBalanceConfigSchema>;
export type CaptainDraftConfig = z.infer<typeof captainDraftConfigSchema>;
export type SelfSelectConfig = z.infer<typeof selfSelectConfigSchema>;

// Host & Lobby
export type HostInstructionsConfig = z.infer<typeof hostInstructionsConfigSchema>;
export type LobbyCodeField = z.infer<typeof lobbyCodeFieldSchema>;
export type LobbyCodeInputConfig = z.infer<typeof lobbyCodeInputConfigSchema>;
export type LobbyCreatedConfig = z.infer<typeof lobbyCreatedConfigSchema>;

// Communication
export type VoiceChannelJoinConfig = z.infer<typeof voiceChannelJoinConfigSchema>;
export type TeamVoiceSplitConfig = z.infer<typeof teamVoiceSplitConfigSchema>;

// Terminal
export type MatchStartConfig = z.infer<typeof matchStartConfigSchema>;
export type MatchCancelledConfig = z.infer<typeof matchCancelledConfigSchema>;
